import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import bookingAPI from '../services/bookingApi';
import Navbar from '../components/Navbar';

const STATUS_COLORS = {
  approved: { bg: '#dcfce7', color: '#16a34a', label: 'Approved' },
  pending:  { bg: '#fef9c3', color: '#ca8a04', label: 'Pending'  },
  rejected: { bg: '#fee2e2', color: '#dc2626', label: 'Rejected' },
};

const FILTER_TABS = ['all', 'pending', 'approved', 'rejected'];

const MyBookingsPage = () => {
  const { user } = useUser();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user?.email) loadBookings();
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBookings = async () => {
    setLoading(true);
    const res = await bookingAPI.getMyBookings(user.email);
    if (res.success) setBookings(res.data);
    setLoading(false);
  };

  const filtered = filter === 'all'
    ? bookings
    : bookings.filter(b => b.status === filter);

  return (
    <div style={styles.page}>
      <Navbar title="My Bookings" backTo="/home" backLabel="Home" />

      {/* Filter tabs */}
      <div style={styles.filterWrap}>
        <div style={styles.filterRow}>
          {FILTER_TABS.map(f => (
            <button
              key={f}
              style={filter === f ? styles.tabActive : styles.tabInactive}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.center}>
            <div style={styles.spinner} />
            <div style={styles.loadingText}>Loading bookings...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={styles.center}>
            <div style={styles.emptyIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div style={styles.emptyTitle}>No bookings found</div>
            <div style={styles.emptySubtitle}>
              {filter === 'all'
                ? "You haven't made any bookings yet."
                : `No ${filter} bookings to show.`}
            </div>
          </div>
        ) : (
          filtered.map(b => {
            const statusStyle = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
            return (
              <div key={b.id} style={styles.card}>
                {/* Card top */}
                <div style={styles.cardTop}>
                  <div>
                    <div style={styles.hallName}>{b.hall_name}</div>
                    {b.event_title && (
                      <div style={styles.eventTitle}>{b.event_title}</div>
                    )}
                  </div>
                  <span style={{ ...styles.badge, backgroundColor: statusStyle.bg, color: statusStyle.color }}>
                    {statusStyle.label}
                  </span>
                </div>

                {/* Details rows */}
                <div style={styles.row}>
                  <span style={styles.label}>Date</span>
                  <span style={styles.value}>{b.booking_date}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Time Slot</span>
                  <span style={styles.value}>{b.time_slot}</span>
                </div>
                <div style={{ ...styles.row, borderBottom: 'none' }}>
                  <span style={styles.label}>Booking ID</span>
                  <span style={styles.value}>{b.booking_id || b.id}</span>
                </div>

                {/* Rejection reason */}
                {b.status === 'rejected' && b.rejection_reason && (
                  <div style={styles.rejectionBox}>
                    <span style={styles.rejectionLabel}>Reason: </span>
                    {b.rejection_reason}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 50%, #dbeafe 100%)',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },

  /* Filter tabs */
  filterWrap: {
    padding: '20px 20px 0',
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  filterRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  tabActive: {
    padding: '8px 20px',
    borderRadius: 999,
    border: 'none',
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    transition: 'all 0.15s ease',
  },
  tabInactive: {
    padding: '8px 20px',
    borderRadius: 999,
    border: '1.5px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    transition: 'all 0.15s ease',
  },

  /* Content area */
  content: {
    padding: '20px 20px 48px',
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxSizing: 'border-box',
  },

  /* Loading */
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 0',
    gap: 12,
  },
  spinner: {
    width: 36,
    height: 36,
    border: '4px solid rgba(99,102,241,0.2)',
    borderTop: '4px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#6b7280',
    fontSize: 15,
  },

  /* Empty state */
  emptyIcon: {
    marginBottom: 4,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: '#1e1b4b',
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
    textAlign: 'center',
  },

  /* Booking card */
  card: {
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 4px 20px rgba(99,102,241,0.1)',
    padding: '20px 22px',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
    gap: 10,
  },
  hallName: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1e1b4b',
    lineHeight: 1.3,
  },
  eventTitle: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 3,
  },
  badge: {
    padding: '4px 13px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    flexShrink: 0,
    alignSelf: 'flex-start',
  },

  /* Detail rows */
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    borderBottom: '1px solid rgba(99,102,241,0.08)',
  },
  label: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: 500,
  },
  value: {
    fontSize: 13,
    color: '#374151',
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '60%',
  },

  /* Rejection reason */
  rejectionBox: {
    marginTop: 12,
    padding: '10px 14px',
    backgroundColor: '#fee2e2',
    borderRadius: 10,
    fontSize: 13,
    color: '#dc2626',
    lineHeight: 1.5,
  },
  rejectionLabel: {
    fontWeight: 600,
  },
};

export default MyBookingsPage;
